import { useChatbotAttachmentsOptional } from 'features/chatbot/contexts/ChatbotAttachmentContext'
import {
  DocumentAgentState,
  useDocumentAgentStateOptional,
} from 'features/knowledgeDocumentViewer'
import { useCallback } from 'react'

/**
 * Builds a context string from attached documents for the AI prompt
 */
function buildAttachmentContextString(
  attachments: { id: string; name: string; type: string | null }[]
): string {
  if (attachments.length === 0) {
    return ''
  }
  const attachmentList = attachments
    .map((a) => {
      const encodedType = a.type ? JSON.stringify(a.type) : '"unknown"'
      return `- ${a.name} (id: ${a.id}, type: ${encodedType})`
    })
    .join('\n')
  return (
    `\n\nThe following documents are attached as context. ` +
    `They are already embedded in Pinecone and can be referenced by their document IDs:\n${attachmentList}`
  )
}

/**
 * Builds a context string from the document viewer state for the AI prompt
 */
function buildDocumentViewerContextString(
  state: DocumentAgentState | undefined
): string {
  if (!state) {
    return ''
  }

  const parts: string[] = []

  if (state.activeTabType === 'document' && state.activeDocumentId) {
    parts.push(
      `The user is currently viewing document ID: ${state.activeDocumentId}. ` +
        `When they refer to "this document", "the document", or "current document", ` +
        `they mean this document.`
    )
  }

  if (
    state.activeTabType === 'timeseries' &&
    state.activeTimeSeriesSensorIds.length > 0
  ) {
    const sensorList = state.activeTimeSeriesSensorIds.join(', ')
    parts.push(
      `The user is currently viewing timeseries data for sensor IDs: [${sensorList}]. ` +
        `When they refer to "this data", "the timeseries", "this tag", or "these sensors", ` +
        `they mean these sensors.`
    )
  }

  if (state.openDocumentIds.length > 0) {
    const docList = state.openDocumentIds.join(', ')
    parts.push(`Open document tabs: [${docList}]`)
  }

  if (state.openTimeSeriesSensorIds.length > 0) {
    const sensorList = state.openTimeSeriesSensorIds.join(', ')
    parts.push(`Open timeseries sensor tabs: [${sensorList}]`)
  }

  if (parts.length === 0) {
    return ''
  }

  return `\n\nDocument Viewer Context:\n${parts.join('\n')}`
}

/**
 * Hook that provides context builder functions for AI prompts.
 * These functions gather context from attachments and document viewer state.
 */
export function useContextBuilders() {
  const attachmentContext = useChatbotAttachmentsOptional()
  const documentAgentState = useDocumentAgentStateOptional()

  const getAttachmentContext = useCallback((): string => {
    const attachments = attachmentContext?.attachments ?? []
    return buildAttachmentContextString(attachments)
  }, [attachmentContext?.attachments])

  const getDocumentViewerContext = useCallback((): string => {
    return buildDocumentViewerContextString(documentAgentState)
  }, [documentAgentState])

  return {
    getAttachmentContext,
    getDocumentViewerContext,
  }
}
